import React, { createContext, useContext, useState, useEffect } from 'react';
import { translations } from '../utils/translations.js';
import { getTranslationValue } from '../utils/translationCoverage.js';
import { safeStorage } from '../utils/storage.js';

export const LanguageContext = createContext();

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within LanguageProvider');
  }
  return context;
};

export const LanguageProvider = ({ children }) => {
  // Get saved language from localStorage or default to 'en'
  const [currentLanguage, setCurrentLanguage] = useState(() => {
    if (typeof window !== 'undefined') {
      const queryLang = new URLSearchParams(window.location.search).get('lang');
      if (queryLang && translations[queryLang]) return queryLang;
    }

    const saved = safeStorage.get('3toolspro_language');
    if (saved && translations[saved]) return saved;

    const runtimeConfigRaw = safeStorage.get('3toolspro_runtime_config_cache');
    if (runtimeConfigRaw) {
      try {
        const runtimeConfig = JSON.parse(runtimeConfigRaw);
        const defaultLang = runtimeConfig?.config?.defaultLanguage;
        if (defaultLang && translations[defaultLang]) return defaultLang;
      } catch {
        // ignore parse issue
      }
    }

    return 'en';
  });

  // Persist language to localStorage when it changes
  useEffect(() => {
    if (typeof window === 'undefined') return;

    safeStorage.set('3toolspro_language', currentLanguage);
    // Update document direction for RTL languages
    document.documentElement.dir = currentLanguage === 'ar' ? 'rtl' : 'ltr';
    document.documentElement.lang = currentLanguage;

    const url = new URL(window.location.href);
    url.searchParams.set('lang', currentLanguage);
    window.history.replaceState({}, '', `${url.pathname}${url.search}${url.hash}`);
  }, [currentLanguage]);

  const t = (key, params = {}) => {
    let text = getTranslationValue(currentLanguage, key);
    
    // Replace placeholders like {count} and {query} with actual values
    Object.keys(params).forEach(param => {
      text = text.replace(new RegExp(`\\{${param}\\}`, 'g'), params[param]);
    });
    
    return text;
  };

  const changeLanguage = (lang) => {
    if (translations[lang]) {
      setCurrentLanguage(lang);
    }
  };

  return (
    <LanguageContext.Provider value={{ currentLanguage, changeLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};
